import Divider from '@mui/material/Divider';
import React, { useState, memo } from 'react';
import { Stack, Typography, Box, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { PriceService } from '../../../api/PriceService';

const PromoCodeLayout = ({ children }) => {
    return (
        <Stack component="section" sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1.5 }} />
            {children}
        </Stack>
    );
};

const PromoCode = memo(function PromoCode({
    hasTickets = false,
    serviceTypeId,
    code = '',
    discountPercentage = null,
    onApply,
}) {
    const { t } = useTranslation();
    const [showForm, setShowForm] = useState(false);
    const [promoCode, setPromoCode] = useState(code);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [isApplied, setIsApplied] = useState(!!code);
    const [discount, setDiscount] = useState(discountPercentage);

    const handleShowForm = () => {
        setShowForm(true);
        setError('');
    };

    const handleInputChange = (event) => {
        setPromoCode(event.target.value);

        if (error) {
            setError('');
        }

        if (discount) {
            setDiscount(null);
        }
    };

    const handleApply = async () => {
        if (!promoCode.trim()) return;

        setIsLoading(true);
        setError('');

        try {
            const response = await PriceService.validatePromoCode(promoCode, serviceTypeId);
            const { valid, discountPercentage, id, type } = response;

            if (valid === true && discountPercentage) {
                // STANDARD codes never stack on ticket prices (Familien-, Partner-,
                // Vorteilsticket); the backend would silently drop them, so refuse up front.
                if (hasTickets && type !== 'UNIVERSAL') {
                    setError('not_valid_with_tickets');
                    return;
                }
                setIsApplied(true);
                setDiscount(discountPercentage);
                onApply?.(id);
            } else {
                setError('unknown');
            }
        } catch (err) {
            // Handle 400 response with { error: "not_valid" }
            if (err.response?.data?.error === 'not_valid') {
                setError('not_valid');
            } else {
                setError('unknown');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemove = () => {
        setPromoCode('');
        setIsApplied(false);
        setError('');
        setDiscount(null);
        setShowForm(false);
        onApply?.(null);
    };

    if (!showForm && !isApplied) {
        return (
            <PromoCodeLayout>
                <Button sx={{ alignSelf: 'center' }} size="small" onClick={handleShowForm}>
                    {t('promo-code.apply')}
                </Button>
            </PromoCodeLayout>
        );
    }

    // TODO add max length 100 chars
    return (
        <PromoCodeLayout>
            <Typography variant="h6" sx={{ mb: 1 }}>
                {t('promo-code.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <TextField
                    fullWidth
                    value={promoCode}
                    onChange={handleInputChange}
                    placeholder={t('promo-code.placeholder')}
                    disabled={isLoading || isApplied}
                    error={!!error}
                    size="small"
                    variant="outlined"
                />
                {!isApplied ? (
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{ pt: '8px', pb: '6px', flexShrink: 0 }}
                        onClick={handleApply}
                        disabled={!promoCode.trim() || isLoading}
                    >
                        {t('promo-code.apply-button')}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{ pt: '8px', pb: '6px', flexShrink: 0 }}
                        size="small"
                        onClick={handleRemove}
                    >
                        {t('promo-code.remove-button')}
                    </Button>
                )}
            </Box>
            {error && (
                <Typography component="div" variant="caption" color="error" sx={{ mt: -0.25, ml: 0.5 }}>
                    {t(error === 'unknown' ? 'promo-code.error.unknown' : `promo-code.error.${error}`)}
                </Typography>
            )}
            {isApplied && discount && (
                <Typography component="div" variant="caption" color="primary" sx={{ mt: -0.5, ml: 0.5 }}>
                    {t('promo-code.success', { discount })}
                </Typography>
            )}
        </PromoCodeLayout>
    );
});

export { PromoCode };
